import { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';

export type Rect = { x: number; y: number; width: number; height: number };
export type CoachTarget = { ref: React.RefObject<View | null>; title: string; text: string };

type Props = {
  targets: CoachTarget[];
  measure: (ref: React.RefObject<View | null>) => Promise<Rect | null>;
  onDone: () => void;
};

/**
 * Coachmarks: σκουραίνει την οθόνη εκτός από το τρέχον στοιχείο (highlight),
 * δείχνει tooltip με επεξήγηση και προχωρά βήμα-βήμα. Tap οπουδήποτε ή «Επόμενο».
 *
 * Ξαναμετράει τη θέση του στόχου σε ΚΑΘΕ βήμα (όχι μία φορά στην αρχή) —
 * περιεχόμενο πάνω από τον στόχο μπορεί να αλλάξει ύψος ασύγχρονα (π.χ. ένα
 * badge που εμφανίζεται αφού φορτώσει δεδομένα από το δίκτυο), οπότε μια
 * στατική μέτρηση στην αρχή θα έδειχνε λάθος σημείο.
 */
export function Coachmarks({ targets, measure, onDone }: Props) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const { width, height } = useWindowDimensions();
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const target = targets[i];
      if (!target) return;
      const first = await measure(target.ref);
      if (cancelled || !mounted.current) return;
      setRect(first);
      // Safety net: κάτι πάνω από τον στόχο μπορεί ακόμα να φορτώνει.
      setTimeout(() => {
        if (cancelled || !mounted.current) return;
        void measure(target.ref).then((r) => { if (!cancelled && mounted.current && r) setRect(r); });
      }, 300);
    }
    void run();
    return () => { cancelled = true; };
  }, [i, targets, measure]);

  if (targets.length === 0) return null;
  const step = targets[i];
  const isLast = i === targets.length - 1;

  function next() {
    if (isLast) onDone();
    else setI((v) => v + 1);
  }

  if (!rect) return null;

  const PAD = 8;
  const hx = Math.max(0, rect.x - PAD);
  const hy = Math.max(0, rect.y - PAD);
  const hw = rect.width + PAD * 2;
  const hh = rect.height + PAD * 2;

  // Tooltip κάτω από το στοιχείο αν χωράει, αλλιώς από πάνω.
  const below = hy + hh + 170 < height;

  return (
    <View style={StyleSheet.absoluteFill}>
      {/* 4 σκούρα panels γύρω από το highlight (το tap προχωρά) */}
      <Pressable style={[styles.dim, { left: 0, top: 0, width, height: hy }]} onPress={next} />
      <Pressable
        style={[styles.dim, { left: 0, top: hy + hh, width, height: Math.max(0, height - (hy + hh)) }]}
        onPress={next}
      />
      <Pressable style={[styles.dim, { left: 0, top: hy, width: hx, height: hh }]} onPress={next} />
      <Pressable
        style={[styles.dim, { left: hx + hw, top: hy, width: Math.max(0, width - (hx + hw)), height: hh }]}
        onPress={next}
      />

      {/* Δαχτυλίδι highlight γύρω από το στοιχείο */}
      <View
        pointerEvents="none"
        style={[styles.ring, { left: hx, top: hy, width: hw, height: hh }]}
      />

      {/* Tooltip */}
      <View
        style={[
          styles.tooltip,
          below ? { top: hy + hh + spacing.md } : { bottom: height - hy + spacing.md },
        ]}
      >
        <Text style={styles.title}>{step.title}</Text>
        <Text style={styles.text}>{step.text}</Text>
        <View style={styles.row}>
          <Pressable onPress={onDone} hitSlop={8} accessibilityRole="button">
            <Text style={styles.skip}>{t('coachmarks.skip')}</Text>
          </Pressable>
          <View style={styles.right}>
            <Text style={styles.counter}>
              {i + 1}/{targets.length}
            </Text>
            <Pressable style={styles.nextBtn} onPress={next} accessibilityRole="button">
              <Text style={styles.nextText}>{isLast ? t('coachmarks.done') : t('coachmarks.next')}</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    dim: {
      position: 'absolute',
      backgroundColor: 'rgba(15, 23, 42, 0.78)',
    },
    ring: {
      position: 'absolute',
      borderWidth: 2,
      borderColor: colors.primaryLight,
      borderRadius: radius.md,
    },
    tooltip: {
      position: 'absolute',
      left: spacing.lg,
      right: spacing.lg,
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      gap: spacing.sm,
      ...shadow.card,
    },
    title: {
      color: colors.text,
      fontSize: fontSize.md,
      fontWeight: '800',
    },
    text: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 20,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: spacing.xs,
    },
    skip: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    right: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
    },
    counter: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    nextBtn: {
      backgroundColor: colors.primary,
      borderRadius: radius.md,
      paddingVertical: 8,
      paddingHorizontal: spacing.lg,
    },
    nextText: {
      color: colors.textOnPrimary,
      fontSize: fontSize.md,
      fontWeight: '700',
    },
  });
}
